import { useState, useMemo } from "react";
import { CreateTableNew as CreateTable, CreateTableWithSelection, SearchBar } from '../../../../common/APICommon';
import { ConfirmationModal } from '../../../../common/Modal';
import DeleteIcon from '@mui/icons-material/DeleteForever';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import GroupIcon from '@mui/icons-material/Group';
import CloseIcon from "@mui/icons-material/Close";

export function EmployeesTab({ form, employeesList, loadingEmployees, handleRemoveEmployee, handleSelectMultiple }) {
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [selectedEmpToRemove, setSelectedEmpToRemove] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const headers = ["Employee Id", "Full Name", "Department", "Position", "Actions"];
  const accessors = ["employeeId", "fullName", "department", "position", "actions"];

  const customCellRenderer = (row, accessor) => {
    if (accessor === "employeeId") return <div className="flex items-center"><div className="bg-gray-100 text-gray-600 px-3 py-1.5 rounded text-xs font-mono">#{row[accessor]}</div></div>;
    if (accessor === "department" || accessor === "position") return <div className="flex items-center"><div className="bg-gray-50 text-gray-700 px-3 py-1.5 rounded-full text-xs border border-gray-200">{row[accessor]}</div></div>;
    return <span className="text-gray-700 px-2">{row[accessor] ?? "-"}</span>;
  };

  const openRemoveConfirm = (emp) => { setSelectedEmpToRemove(emp); setShowRemoveConfirm(true); };
  const handleConfirmRemove = () => { if (selectedEmpToRemove) { handleRemoveEmployee(selectedEmpToRemove.employeePersonalInformationId); setShowRemoveConfirm(false); setSelectedEmpToRemove(null); } };

  const actions = [{ name: "Remove", tooltip: "remove employee", icon: <DeleteIcon fontSize="small" />, className: "bg-red-500 hover:bg-red-600", function: openRemoveConfirm, visible: true }];

  const filteredSubordinates = useMemo(() => {
    if (!employeeSearchTerm || !form.employees) return form.employees || [];
    const lower = employeeSearchTerm.toLowerCase();
    return form.employees.filter(emp => emp.employeeId?.toString().includes(lower) || emp.fullName?.toLowerCase().includes(lower));
  }, [form.employees, employeeSearchTerm]);

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-gray-300 bg-white p-4 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <h3 className="font-medium text-gray-700">Selected Employees ({form.employees?.length || 0})</h3>
          <div className="flex items-center space-x-2">
            {form.employees?.length > 0 && <SearchBar handleFilter={setEmployeeSearchTerm} placeholder="Search subordinates..." />}
            <button onClick={() => setShowModal(true)} className="flex items-center justify-center space-x-2 rounded border-none bg-green-500 px-4 py-2 text-white hover:bg-green-600 cursor-pointer transition-colors"><PersonAddIcon fontSize="small" /><span>Add Subordinate(s)</span></button>
          </div>
        </div>
        {form.employees?.length > 0 ? (
          <div className="mt-4"><CreateTable tableName={"Subordinates"} headers={headers} accessors={accessors} data={filteredSubordinates} actions={actions} currentPage={currentPage} setCurrentPage={setCurrentPage} itemsPerPage={itemsPerPage} totalItems={filteredSubordinates.length} customCellRenderer={customCellRenderer} /></div>
        ) : (
          <div className="mt-4 text-center py-8 text-gray-500"><GroupIcon className="mx-auto mb-3 text-4xl text-gray-300" /><p>No subordinates assigned.</p></div>
        )}
      </div>
      {showModal && <SelectionModal employees={employeesList} loading={loadingEmployees} onClose={() => setShowModal(false)} onSelect={handleSelectMultiple} existing={form.employees} managerId={form.employeePersonalInformationId} />}
      <ConfirmationModal visible={showRemoveConfirm} onClose={() => setShowRemoveConfirm(false)} onConfirm={handleConfirmRemove} title="Remove Subordinate" message={`Are you sure you want to remove ${selectedEmpToRemove?.fullName}?`} confirmText="Remove" type="danger" />
    </div>
  );
}

function SelectionModal({ employees, loading, onClose, onSelect, existing, managerId }) {
  const [selected, setSelected] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");

  const filteredEmployees = useMemo(() => {
    const list = employees.filter(e => e.id !== managerId);
    if (!searchTerm) return list;
    const lower = searchTerm.toLowerCase();
    return list.filter(e => e.fullName?.toLowerCase().includes(lower) || e.employeeId?.toString().includes(searchTerm));
  }, [employees, searchTerm, managerId]);

  const formattedEmployees = useMemo(() => {
    return filteredEmployees.map(e => ({
      ...e,
      department: e.employeeInformation?.departmentDescription || e.employeeInformationDepartmentDescription || e.department || "-",
      position: e.employeeInformation?.positionDescription || e.employeeInformationPositionDescription || e.position || "-",
    }));
  }, [filteredEmployees]);

  const isEmployeeDisabled = (employee) => existing.some(ex => ex.employeePersonalInformationId === employee.id);

  return (
    <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/50 p-3">
      <div className="relative flex h-auto max-h-[80vh] w-full max-w-6xl flex-col rounded-lg bg-white shadow-xl">
        <div className="sticky top-0 z-10 flex items-center justify-between rounded-t-lg border-b border-gray-200 bg-white px-6 py-2 shadow-sm">
          <h2 className="text-xl font-semibold text-gray-800">Select Subordinates</h2>
          <button onClick={onClose} className="border-none bg-transparent p-1 text-gray-400 hover:text-gray-700 cursor-pointer"><CloseIcon fontSize="medium" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          <div className="mb-4"><SearchBar handleFilter={setSearchTerm} /></div>
          {loading ? <div className="text-center py-8 text-gray-600">Loading...</div> : (
            <CreateTableWithSelection headers={["Employee Id", "Full Name", "Department", "Position"]} accessors={["employeeId", "fullName", "department", "position"]} data={formattedEmployees} selectedItems={selected} onSelectItem={(item) => { const isSelected = selected.some(s => s.id === item.id); setSelected(isSelected ? selected.filter(s => s.id !== item.id) : [...selected, item]); }} onSelectAll={(items) => setSelected(items)} selectionDisabledChecker={isEmployeeDisabled} selectionDisabledMessage="Already added" currentPage={currentPage} setCurrentPage={setCurrentPage} itemsPerPage={10} totalItems={formattedEmployees.length} />
          )}
        </div>
        <div className="flex items-center justify-between rounded-b-lg border-t border-gray-200 bg-white p-5 shadow-sm">
          <div className="text-sm text-gray-600"><span className="font-medium">{selected.length}</span> employee(s) selected</div>
          <div className="flex space-x-2">
            <button onClick={onClose} className="rounded border-none bg-gray-400 px-4 py-2 text-white hover:bg-gray-500 cursor-pointer">Cancel</button>
            <button onClick={() => { onSelect(selected); onClose(); }} disabled={selected.length === 0} className="rounded border-none bg-blue-500 px-4 py-2 text-white hover:bg-blue-600 disabled:bg-gray-300 cursor-pointer">Add Selected ({selected.length})</button>
          </div>
        </div>
      </div>
    </div>
  );
}